import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ContentCopy as CopyIcon,
  LockOutlined as LockIcon,
  Search as SearchIcon,
  SubdirectoryArrowRight as StepIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser, isOtherCustomer } from '../utils/auth';
import {
  MAX_REQUEST_ID_CHARS,
  RequestLookupResult,
  parseRequestId,
  requestLookupApi,
} from '../services/requestLookupApi';

type Kind = 'function' | 'chain' | 'chain step';

interface TimelineRow {
  key: string;
  kind: Kind;
  id: string;
  name: string;
  detail: string | null;
  status: string;
  model: string | null;
  durationMs: number | null;
  at: number | null;
  error: string | null;
  redacted: boolean;
  nested: boolean;
}

const parseUtc = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(/(Z|[+-]\d\d:\d\d)$/.test(iso) ? iso : `${iso}Z`).getTime();
  return Number.isFinite(t) ? t : null;
};

const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.round((ms % 3_600_000) / 60_000)}m`;
};

const formatTime = (t: number | null): string => (t === null ? '-' : new Date(t).toLocaleString());

const byTime = (a: { at: number | null }, b: { at: number | null }) =>
  (a.at ?? Number.MAX_SAFE_INTEGER) - (b.at ?? Number.MAX_SAFE_INTEGER);

const statusChip = (status: string) => {
  const s = status.toLowerCase();
  const color =
    s === 'success' || s === 'completed' ? 'success'
    : s === 'failed' ? 'error'
    : s === 'paused' ? 'warning'
    : s === 'running' || s === 'pending' ? 'info'
    : 'default';
  const label = s === 'completed' ? 'Success' : status.charAt(0).toUpperCase() + status.slice(1);
  return <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
};

/** Chains first-level, their step runs right after them; standalone runs in time order between. */
function buildTimeline(result: RequestLookupResult): TimelineRow[] {
  const chainIds = new Set(result.chain_executions.map(c => c.id));
  const stepsByChain = new Map<string, TimelineRow[]>();
  const top: TimelineRow[] = [];

  result.function_runs.forEach(r => {
    const inChain = Boolean(r.chain_execution_id && chainIds.has(r.chain_execution_id));
    const row: TimelineRow = {
      key: `run-${r.id}`,
      kind: r.chain_execution_id ? 'chain step' : 'function',
      id: r.id,
      name: r.function_name,
      detail: [
        r.function_version ? `v${r.function_version}` : null,
        r.node_id ? `step ${r.node_id}${r.mapping_index !== null ? ` #${r.mapping_index + 1}` : ''}` : null,
      ].filter(Boolean).join(' · ') || null,
      status: r.success ? 'success' : 'failed',
      model: r.model_used,
      durationMs: r.execution_time_ms,
      at: parseUtc(r.created_at),
      error: r.error,
      redacted: r.content_redacted,
      nested: inChain,
    };
    if (inChain) {
      const list = stepsByChain.get(r.chain_execution_id!) ?? [];
      list.push(row);
      stepsByChain.set(r.chain_execution_id!, list);
    } else {
      top.push(row);
    }
  });

  result.chain_executions.forEach(c => {
    top.push({
      key: `chain-${c.id}`,
      kind: 'chain',
      id: c.id,
      name: c.chain_name || 'Chain',
      detail: c.source_execution_id ? 'rerun' : null,
      status: c.status,
      model: null,
      durationMs: c.execution_time_ms,
      at: parseUtc(c.started_at) ?? parseUtc(c.completed_at),
      error: c.error,
      redacted: c.content_redacted,
      nested: false,
    });
  });

  return top.sort(byTime).flatMap(row =>
    row.kind === 'chain' ? [row, ...(stepsByChain.get(row.id) ?? []).sort(byTime)] : [row]
  );
}

const WhereToFind: React.FC = () => (
  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
    <li>
      <Typography variant="body2">
        The <code>X-Request-ID</code> header on a Passio platform API response.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        The <code>req-id</code> field in platform logs.
      </Typography>
    </li>
  </Box>
);

const MissingNote: React.FC = () => (
  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
    Runs are matched by the ID the platform sent with each call. Very old runs, and runs whose content was removed
    for data retention before this lookup was added, may not have it.
  </Typography>
);

export const RequestLookup: React.FC = () => {
  const navigate = useNavigate();
  const { permissions, getIdToken } = useAuth();
  const api = useMemo(() => requestLookupApi(getIdToken), [getIdToken]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = parseRequestId(searchParams.get('id'));
  const urlCustomer = (searchParams.get('customer_id') || '').trim();
  const isAdmin = isAdminUser(permissions?.userType);
  const viewingOther = isOtherCustomer(urlCustomer, permissions);

  const [input, setInput] = useState(urlId);
  const [customerInput, setCustomerInput] = useState(urlCustomer);
  const [result, setResult] = useState<RequestLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => setInput(urlId), [urlId]);
  useEffect(() => setCustomerInput(urlCustomer), [urlCustomer]);

  useEffect(() => {
    if (!urlId) {
      setResult(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .lookup(urlId, viewingOther ? urlCustomer : null)
      .then(data => {
        if (!cancelled) setResult(data);
      })
      .catch(err => {
        if (cancelled) return;
        setResult(null);
        setError(err instanceof Error ? err.message : 'Failed to look up request');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, urlId, urlCustomer, viewingOther]);

  const parsedInput = parseRequestId(input);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsedInput) return;
    const next = new URLSearchParams({ id: parsedInput });
    const customer = customerInput.trim();
    if (isAdmin && customer) next.set('customer_id', customer);
    setSearchParams(next);
  };

  const timeline = useMemo(() => (result ? buildTimeline(result) : []), [result]);
  const firstAt = parseUtc(result?.first_at);
  const lastAt = parseUtc(result?.last_at);
  const failures = result
    ? result.function_runs.filter(r => !r.success).length + result.chain_executions.filter(c => c.status === 'failed').length
    : 0;
  const stepRuns = result ? result.function_runs.filter(r => r.chain_execution_id).length : 0;
  const empty = result && !result.function_runs.length && !result.chain_executions.length;

  const linkQuery = viewingOther && result
    ? `?${new URLSearchParams({ customer_id: result.customer_id, ...(result.customer_tag ? { customer_tag: result.customer_tag } : {}) })}`
    : '';
  const rowPath = (row: TimelineRow) =>
    `${row.kind === 'chain' ? '/dashboard/chain-run/' : '/dashboard/run/'}${row.id}${linkQuery}`;

  const copyId = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.request_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Request Lookup</Typography>
        <Typography variant="body2" color="text.secondary">
          Every function run and chain run recorded for one Passio platform request, in order.
        </Typography>
      </Box>

      <Paper component="form" onSubmit={submit} sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          label="Platform request ID"
          placeholder="Paste an X-Request-ID, or a log line such as req-id: 0192f0c4-…"
          value={input}
          onChange={e => setInput(e.target.value)}
          autoFocus={!urlId}
          inputProps={{ spellCheck: false, maxLength: MAX_REQUEST_ID_CHARS * 4 }}
          helperText={input.trim() && parsedInput !== input.trim() ? `Will search for ${parsedInput || 'nothing'}` : ' '}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { fontFamily: 'monospace', fontSize: '1.05rem' },
          }}
          sx={{ flex: '1 1 420px' }}
        />
        {isAdmin && (
          <TextField
            label="Customer ID or tag"
            placeholder="Your account"
            value={customerInput}
            onChange={e => setCustomerInput(e.target.value)}
            helperText="Admins: account to search"
            sx={{ flex: '0 1 240px' }}
          />
        )}
        <Button type="submit" variant="contained" size="large" disabled={!parsedInput || loading} sx={{ mt: 0.5 }}>
          Look up
        </Button>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !urlId && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Where to find the request ID
          </Typography>
          <WhereToFind />
          <MissingNote />
        </Paper>
      )}

      {!loading && result && empty && (
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            No runs found for <code>{result.request_id}</code> in{' '}
            {viewingOther ? `customer ${result.customer_tag || result.customer_id}` : 'this account'}.
            {isAdmin && ' Platform calls are usually made with the platform\'s own account; try that customer.'}
          </Typography>
          <Typography variant="body2">Check the ID came from:</Typography>
          <WhereToFind />
          <MissingNote />
        </Alert>
      )}

      {!loading && result && !empty && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {result.request_id}
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy request ID'}>
              <IconButton size="small" onClick={copyId} aria-label="Copy request ID">
                {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {viewingOther && <Chip size="small" variant="outlined" label={`Customer ${result.customer_tag || result.customer_id}`} />}
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Function runs', value: result.function_runs.length.toLocaleString(), note: stepRuns ? `${stepRuns} as chain steps` : null },
              { label: 'Chain runs', value: result.chain_executions.length.toLocaleString(), note: null },
              { label: 'Failures', value: failures.toLocaleString(), note: null, error: failures > 0 },
              { label: 'First', value: formatTime(firstAt), note: null },
              { label: 'Last', value: formatTime(lastAt), note: null },
              {
                label: 'Total span',
                value: firstAt !== null && lastAt !== null ? formatDuration(lastAt - firstAt) : '-',
                note: null,
              },
            ].map(card => (
              <Grid item xs={6} md={4} lg={2} key={card.label}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {card.label}
                    </Typography>
                    <Typography variant="h6" color={card.error ? 'error.main' : undefined} sx={{ wordBreak: 'break-word' }}>
                      {card.value}
                    </Typography>
                    {card.note && (
                      <Typography variant="caption" color="text.secondary">
                        {card.note}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {result.truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This request has more than 500 function runs or chain runs; only the first 500 of each are shown.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Duration</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell align="right">Offset</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeline.map(row => (
                  <TableRow
                    key={row.key}
                    hover
                    onClick={() => navigate(rowPath(row))}
                    sx={{ cursor: 'pointer', ...(row.nested ? { bgcolor: 'action.hover' } : {}) }}
                  >
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color={row.kind === 'chain' ? 'primary' : 'text.primary'}>
                        {row.kind}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: row.nested ? 2 : 0 }}>
                        {row.nested && <StepIcon fontSize="small" color="action" />}
                        <Link
                          component={RouterLink}
                          to={rowPath(row)}
                          underline="hover"
                          color="inherit"
                          onClick={e => e.stopPropagation()}
                          sx={{ fontWeight: row.kind === 'chain' ? 600 : 400 }}
                        >
                          {row.name}
                        </Link>
                        {row.detail && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.detail}
                          </Typography>
                        )}
                        {row.redacted && (
                          <Tooltip title="Usage only: input and output were not stored">
                            <LockIcon fontSize="inherit" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                      {row.error && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', pl: row.nested ? 4.5 : 0 }} noWrap title={row.error}>
                          {row.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.model || '-'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDuration(row.durationMs)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(row.at)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {row.at !== null && firstAt !== null ? `+${formatDuration(row.at - firstAt)}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Function run times are when the run was recorded (as it finished); chain times are when the chain started.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default RequestLookup;
